import { TTypeOfCodec } from "../../../shared/src/codecs/codec";
import { array, TArrayCodec } from "../../../shared/src/codecs/types/array";
import { dateTime } from "../../../shared/src/codecs/types/dateTime";
import { boolean, TBooleanCodec } from "../../../shared/src/codecs/types/boolean";
import { form, TFormCodec } from "../../../shared/src/codecs/types/form";
import { intersection, TIntersectionCodec } from "../../../shared/src/codecs/types/intersection";
import { literal, TLiteralCodec } from "../../../shared/src/codecs/types/literal";
import { nullCodec, TNullCodec } from "../../../shared/src/codecs/types/nullCodec";
import { required, TRequiredCodec } from "../../../shared/src/codecs/types/required";
import { requiredFlatOverloaded, TRequiredFlatOverloadedCodec } from "../../../shared/src/codecs/types/requiredFlatOverloaded";
import { string, TAnyStringCodec } from "../../../shared/src/codecs/types/string";
import { TUnionCodec, union } from "../../../shared/src/codecs/types/union";
import { TUuidCodec, uuid } from "../../../shared/src/codecs/types/uuid";
import { Address2, Address5, TAddress2Codec, TAddress5Codec } from "../Address";
import { CaseId, TCaseIdCodec } from "../CaseId";
import { CaseEditableFull, Cases14, TCaseEditableFullCodec, TCases14Codec, CaseRootReadOnlyProps, TCaseRootReadOnlyPropsCodec, TCaseEnquiryTimestampsCodec,
    CaseEnquiryTimestamps, 
    CasesSdlt,
    TCasesSdltCodec,
    TCaseStatusCodec,
    CaseStatus,
} from "../Cases";
import { CasesBankAccount3, TCasesBankAccount3Codec, CasesBankAccount4, TCasesBankAccount4Codec } from "../CasesBankAccount";
import { TCasesRelatedTransaction2Codec, CasesRelatedTransaction2 } from "../CasesRelatedTransaction";
import { CasesRiskAssessment1, CasesRiskAssessment2, TCasesRiskAssessment1Codec, TCasesRiskAssessment2Codec } from "../CasesRiskAssessment";
import { EmptyObject, TEmptyObjectCodec } from "../EmptyObject";
import { CaseClientNames, TCaseClientNames } from "../overloads/CaseClientNames";
import { CaseSdltAnswersSubmissionStatus, TCaseSdltAnswersSubmissionStatusCodec } from "../overloads/CaseSdltAnswersSubmissionStatus";
import { TUserNameCodec, TUserNamesCodec, UserName, UserNames } from "../overloads/UserName";
import { TUsersVideoVerificationCallHasExpiredCodec, UsersVideoVerificationCallHasExpired } from "../overloads/UsersVideoVerificationCallHasExpired";
import { UserBookableForVideoCall, TUserBookableForVideoCallCodec, TUserBankruptcySearchCodec, UserBankruptcySearch } from "../User";
import { TUserVideoVerificationCall1Codec, TUserVideoVerificationCall2Codec, UserVideoVerificationCall1, UserVideoVerificationCall2 } from "../UserVideoVerificationCall";
import { TVideoVerificationCall1Codec, TVideoVerificationCall2Codec, VideoVerificationCall1, VideoVerificationCall2 } from "../VideoVerificationCall";
import { CaseMemberForm, TCaseMemberFormCodec } from "./CaseMemberForm";
import { longString } from "../../../shared/src/codecs/types/longString";
import { TBankruptcySearchWithUsersCodec, BankruptcySearchWithUsers} from "../BankruptcySearch";
import { Introducer1, TIntroducer1Codec, BankAccountIntroducer, TBankAccountIntroducerCodec } from "../Introducer";
import { TUuidObjectCodec, UuidObject } from "../Id";
import { ReadyForExchangeChecklist, TReadyForExchangeChecklistCodec } from "../ReadyForExchangeChecklist";
import { CaseQuote, CaseCreateQuoteFilled, CaseCreateQuoteUnfilled, TCaseQuoteCodec, TCaseCreateQuoteFilledCodec, TCaseCreateQuoteUnfilledCodec, TCaseQuoteSentToClientCodec, CaseQuoteSentToClient, CaseQuoteAddAdditionalItem, TCaseQuoteAddAdditionalItemCodec, CaseQuoteRemoveAdditionalItem, TCaseQuoteRemoveAdditionalItemCodec, QuoteAdditionalItemOption, TQuoteAdditionalItemOptionCodec } from "../Quote";
import { positiveInteger } from "../../../shared/src/codecs/types/positiveInteger";
import { CasesEpc1, TCasesEpc1Codec } from "../CasesEpc";
import { CaseEpcSearchForm, TCaseEpcSearchFormCodec, CaseEpcAttachForm, TCaseEpcAttachFormCodec, CaseEpcRerunLookupForm, TCaseEpcRerunLookupFormCodec, CaseEpcMarkNotRequiredForm, TCaseEpcMarkNotRequiredFormCodec, CaseEpcSetSortedForm, TCaseEpcSetSortedFormCodec } from "./CaseEpcForm";

export const CaseBankAccountForm: TFormCodec<TCasesBankAccount3Codec, TCasesBankAccount4Codec> = form(
    CasesBankAccount3,
    CasesBankAccount4,
);
export type TCaseBankAccountFormCodec = typeof CaseBankAccountForm;
export type TCaseBankAccountForm = TTypeOfCodec<TCaseBankAccountFormCodec>;

// Staff-side "Send proceeds to" selection: route a case's payout to the case members
// ("client") or to the case's introducer ("introducer"). Selecting "introducer"
// ensures a live introducer-recipient bank account exists, parks all other accounts as
// won't-be-used, and locks bank-detail changes so the bank-accounts roadblock resolves.
export const CaseSetPayoutRecipient = required({
    case_id: uuid(),
    recipient: union([literal("client"), literal("introducer")]),
});
export type TCaseSetPayoutRecipientCodec = typeof CaseSetPayoutRecipient;

export const CaseSetPayoutRecipientForm: TFormCodec<TCaseSetPayoutRecipientCodec, TEmptyObjectCodec> = form(
    CaseSetPayoutRecipient,
    EmptyObject,
);
export type TCaseSetPayoutRecipientFormCodec = typeof CaseSetPayoutRecipientForm;
export type TCaseSetPayoutRecipientForm = TTypeOfCodec<TCaseSetPayoutRecipientFormCodec>;

export const CaseSignOffAsReadyForExchangeForm: TFormCodec<TCases14Codec, TEmptyObjectCodec> = form(
    Cases14,
    EmptyObject,
);
export type TCaseSignOffAsReadyForExchangeFormCodec = typeof CaseSignOffAsReadyForExchangeForm;
export type TCaseSignOffAsReadyForExchangeForm = TTypeOfCodec<TCaseSignOffAsReadyForExchangeFormCodec>;

export const CaseInstructForm: TFormCodec<TCases14Codec, TEmptyObjectCodec> = form(
    Cases14,
    EmptyObject,
);
export type TCaseInstructFormCodec = typeof CaseInstructForm;
export type TCaseInstructForm = TTypeOfCodec<TCaseInstructFormCodec>;

export const CaseRelatedTransactionCreateSearchResultsForm: TFormCodec<TRequiredCodec<{from_case_id: TUuidCodec, to_case_id: TUuidCodec}>, TIntersectionCodec<[
    TRequiredFlatOverloadedCodec<{addresses: TAddress5Codec}>,
    TRequiredFlatOverloadedCodec<{client_names: TCaseClientNames}>,
    TRequiredCodec<{status: TCaseStatusCodec}>,
]>> = form(
    required({
        from_case_id: uuid(),
        to_case_id: uuid(),
    }),
    intersection([
        requiredFlatOverloaded({
            addresses: Address5,
        }),
        requiredFlatOverloaded({
            client_names: CaseClientNames,
        }),
        required({
            status: CaseStatus,
        })
    ])
);
export type TCaseRelatedTransactionCreateSearchResultsFormCodec = typeof CaseRelatedTransactionCreateSearchResultsForm;
export type TCaseRelatedTransactionCreateSearchResultsForm = TTypeOfCodec<TCaseRelatedTransactionCreateSearchResultsFormCodec>;

export const CaseDualRepSearchResultsForm: TFormCodec<TRequiredCodec<{from_case_id: TUuidCodec, to_case_id: TUuidCodec}>, TIntersectionCodec<[
    TRequiredFlatOverloadedCodec<{addresses: TAddress5Codec}>,
    TRequiredCodec<{
        klyant_matter_id: TAnyStringCodec,
        status: TCaseStatusCodec,
    }>,
]>> = form(
    required({
        from_case_id: uuid(),
        to_case_id: uuid(),
    }),
    intersection([
        requiredFlatOverloaded({
            addresses: Address5,
        }),
        required({
            klyant_matter_id: string(),
            status: CaseStatus,
        })
    ])
);
export type TCaseDualRepSearchResultsFormCodec = typeof CaseDualRepSearchResultsForm;
export type TCaseDualRepSearchResultsForm = TTypeOfCodec<TCaseDualRepSearchResultsFormCodec>;

export const CaseRelatedTransactionCreateForm: TFormCodec<TRequiredCodec<{case_id: TUuidCodec, search_term: TAnyStringCodec}>, TArrayCodec<TCaseRelatedTransactionCreateSearchResultsFormCodec>> = form(
    required({
        case_id: uuid(),
        search_term: string(),
    }),
    array(CaseRelatedTransactionCreateSearchResultsForm),
);
export type TCaseRelatedTransactionCreateFormCodec = typeof CaseRelatedTransactionCreateForm;
export type TCaseRelatedTransactionCreateForm = TTypeOfCodec<TCaseRelatedTransactionCreateFormCodec>;

export const CaseDualRepCreateForm: TFormCodec<
    TRequiredCodec<{
        case_id: TUuidCodec, search_term: TAnyStringCodec
    }>,
    TArrayCodec<TCaseDualRepSearchResultsFormCodec>
> = form(
    required({
        case_id: uuid(),
        search_term: string(),
    }),
    array(CaseDualRepSearchResultsForm),
);
export type TCaseDualRepCreateFormCodec = typeof CaseDualRepCreateForm;
export type TCaseDualRepCreateForm = TTypeOfCodec<TCaseDualRepCreateFormCodec>;

const CaseRelatedTransactionInternalOrExternal = required({
    internal_or_external: union([
        literal("internal"),
        literal("external"),
    ]),
});
type CaseRelatedTransactionInternalOrExternalCodec = typeof CaseRelatedTransactionInternalOrExternal;

export const CaseRelatedTransactionForm1: TFormCodec<TRequiredCodec<{id: TUuidCodec}>, TIntersectionCodec<[
    TRequiredFlatOverloadedCodec<{address: TAddress2Codec}>,
    TRequiredCodec<{related_case_id: TUnionCodec<[TUuidCodec, TNullCodec]>}>,
    CaseRelatedTransactionInternalOrExternalCodec,
]>> = form(
    required({
        id: uuid(),
    }),
    intersection([
        requiredFlatOverloaded({
            address: Address2,
        }),
        required({
            related_case_id: union([uuid(), nullCodec()]),
        }),
        CaseRelatedTransactionInternalOrExternal,
    ]),
);
export type TCaseRelatedTransactionForm1Codec = typeof CaseRelatedTransactionForm1;
export type TCaseRelatedTransactionForm1 = TTypeOfCodec<TCaseRelatedTransactionForm1Codec>;

export const CaseRelatedTransactionForm2: TFormCodec<TRequiredCodec<{id: TUuidCodec}>, TIntersectionCodec<[
    TRequiredFlatOverloadedCodec<{address: TAddress5Codec}>,
    TRequiredCodec<{related_case_id: TUnionCodec<[TUuidCodec, TNullCodec]>}>,
    CaseRelatedTransactionInternalOrExternalCodec,
]>> = form(
    required({
        id: uuid(),
    }),
    intersection([
        requiredFlatOverloaded({
            address: Address5,
        }),
        required({
            related_case_id: union([uuid(), nullCodec()]),
        }),
        CaseRelatedTransactionInternalOrExternal,
    ])
);
export type TCaseRelatedTransactionForm2Codec = typeof CaseRelatedTransactionForm2;
export type TCaseRelatedTransactionForm2 = TTypeOfCodec<TCaseRelatedTransactionForm2Codec>;

export const CaseRelatedTransactionDataForm: TFormCodec<TCasesRelatedTransaction2Codec, TRequiredCodec<{
    linked_to: TUnionCodec<[TCaseRelatedTransactionForm1Codec, TNullCodec]>
}>> = form(CasesRelatedTransaction2, required({
    linked_to: union([CaseRelatedTransactionForm1, nullCodec()]),
}));
export type TCaseRelatedTransactionDataFormCodec = typeof CaseRelatedTransactionDataForm;
export type TCaseRelatedTransactionDataForm = TTypeOfCodec<TCaseRelatedTransactionDataFormCodec>;

export const CaseDualRepOurSideConsent = intersection([
    requiredFlatOverloaded({
        name: UserName,
    }),
    required({
        has_consented: boolean()
    }),
]);
export type TCaseDualRepOurSideConsentCodec = typeof CaseDualRepOurSideConsent;
export type TCaseDualRepOurSideConsent = TTypeOfCodec<TCaseDualRepOurSideConsentCodec>;

export const CaseDualRepLinkedForm: TFormCodec<
    TUuidObjectCodec,
    TIntersectionCodec<[
        TRequiredFlatOverloadedCodec<{
            address: TAddress5Codec,
        }>,
        TRequiredCodec<{
            type: TUnionCodec<[TLiteralCodec<"original">, TLiteralCodec<"dual_rep">]>,
            klyant_matter_id: TAnyStringCodec,
            other_case_id: TUuidCodec,
            other_case_status: TCaseStatusCodec,
            at_least_one_our_side_clients_consent: TBooleanCodec,
            our_side_client_consents: TArrayCodec<TCaseDualRepOurSideConsentCodec>
            at_least_one_other_side_clients_consent: TBooleanCodec,
            our_case_can_be_worked_on: TBooleanCodec,
            other_case_purchasing_searches: TBooleanCodec,
            can_send_instruct_emails: TBooleanCodec,
        }>,
        TRequiredFlatOverloadedCodec<{
            other_case_handler_name: TUnionCodec<[
                TNullCodec,
                TUserNameCodec,
            ]>,
        }>,
        TRequiredFlatOverloadedCodec<{
            other_case_assistant_name: TUnionCodec<[
                TNullCodec,
                TUserNameCodec,
            ]>,
        }>,
    ]>
> = form(
    UuidObject,
    intersection([
        requiredFlatOverloaded({
            address: Address5,
        }),
        required({
            type: union([literal("original"), literal("dual_rep")]),
            klyant_matter_id: string(),
            other_case_id: uuid(),
            other_case_status: CaseStatus,
            at_least_one_our_side_clients_consent: boolean(),
            our_side_client_consents: array(CaseDualRepOurSideConsent),
            at_least_one_other_side_clients_consent: boolean(),
            our_case_can_be_worked_on: boolean(),
            other_case_purchasing_searches: boolean(),
            can_send_instruct_emails: boolean(),
        }),
        requiredFlatOverloaded({
            other_case_handler_name: union([nullCodec(), UserName]),
        }),
        requiredFlatOverloaded({
            other_case_assistant_name: union([nullCodec(), UserName]),
        }),
    ]),
);
export type TCaseDualRepLinkedFormCodec = typeof CaseDualRepLinkedForm;
export type TCaseDualRepLinkedForm = TTypeOfCodec<TCaseDualRepLinkedFormCodec>;

// The agency link between a case and the Sail Homes listing marketing the same property.
// Deliberately NOT named "linked listing" in anything staff read — the case page already
// shows a dual-rep "Linked case", and two adjacent controls both saying linked mean very
// different things. See CONTEXT.md, "Adding and removing the agency link by hand".

const AgencyLinkRelationship = union([
    literal("successor"),
    literal("predecessor"),
    literal("unrelated"),
]);
type TAgencyLinkRelationshipCodec = typeof AgencyLinkRelationship;

// Dates and enums arrive as ::text from the search query. The picker only displays them,
// so a string avoids a decode failure on a listings enum this form does not otherwise care
// about.
export const CaseListingSearchResultForm: TFormCodec<
    TRequiredCodec<{
        case_id: TUuidCodec,
        listing_id: TUuidCodec,
    }>,
    TIntersectionCodec<[
        TRequiredFlatOverloadedCodec<{
            address_short_string: TAddress2Codec,
        }>,
        TRequiredCodec<{
            status: TAnyStringCodec,
            instructed_date: TUnionCodec<[TAnyStringCodec, TNullCodec]>,
            is_lost: TBooleanCodec,
            has_completed: TBooleanCodec,
            seller_names: TAnyStringCodec,
            shares_a_party_with_the_case: TBooleanCodec,
            already_linked_to_case_id: TUnionCodec<[TUuidCodec, TNullCodec]>,
            already_linked_relationship: TUnionCodec<[TAgencyLinkRelationshipCodec, TNullCodec]>,
        }>,
    ]>
> = form(
    required({
        case_id: uuid(),
        listing_id: uuid(),
    }),
    intersection([
        requiredFlatOverloaded({
            address_short_string: Address2,
        }),
        required({
            status: string(),
            instructed_date: union([string(), nullCodec()]),
            is_lost: boolean(),
            has_completed: boolean(),
            seller_names: string(),
            shares_a_party_with_the_case: boolean(),
            already_linked_to_case_id: union([uuid(), nullCodec()]),
            already_linked_relationship: union([AgencyLinkRelationship, nullCodec()]),
        }),
    ]),
);
export type TCaseListingSearchResultFormCodec = typeof CaseListingSearchResultForm;
export type TCaseListingSearchResultForm = TTypeOfCodec<TCaseListingSearchResultFormCodec>;

export const CaseListingSearchForm: TFormCodec<
    TRequiredCodec<{
        case_id: TUuidCodec,
        search_term: TAnyStringCodec,
    }>,
    TArrayCodec<TCaseListingSearchResultFormCodec>
> = form(
    required({
        case_id: uuid(),
        search_term: string(),
    }),
    array(CaseListingSearchResultForm),
);
export type TCaseListingSearchFormCodec = typeof CaseListingSearchForm;
export type TCaseListingSearchForm = TTypeOfCodec<TCaseListingSearchFormCodec>;

// Doubles as the unlink form — the tile needs the listing's details to render, and unlink
// needs nothing but the case id. `has_completed` and `is_lost` are here because the
// sailboat badge hides for both, so the tile has to say the link exists even when no badge
// does (GetCaseController.getSailHomesCase is narrower than the link itself).
export const CaseLinkedListingForm: TFormCodec<
    TRequiredCodec<{
        case_id: TUuidCodec,
    }>,
    TIntersectionCodec<[
        TRequiredFlatOverloadedCodec<{
            address_short_string: TAddress2Codec,
        }>,
        TRequiredCodec<{
            listing_id: TUuidCodec,
            status: TAnyStringCodec,
            instructed_date: TUnionCodec<[TAnyStringCodec, TNullCodec]>,
            is_lost: TBooleanCodec,
            has_completed: TBooleanCodec,
        }>,
    ]>
> = form(
    required({
        case_id: uuid(),
    }),
    intersection([
        requiredFlatOverloaded({
            address_short_string: Address2,
        }),
        required({
            listing_id: uuid(),
            status: string(),
            instructed_date: union([string(), nullCodec()]),
            is_lost: boolean(),
            has_completed: boolean(),
        }),
    ]),
);
export type TCaseLinkedListingFormCodec = typeof CaseLinkedListingForm;
export type TCaseLinkedListingForm = TTypeOfCodec<TCaseLinkedListingFormCodec>;

export const CaseBankruptcySearchesListForm: TFormCodec<
    TCaseIdCodec,
    TRequiredCodec<{
        bankruptcy_searches: TArrayCodec<TBankruptcySearchWithUsersCodec>
    }>
> = form(
    CaseId,
    required({
        bankruptcy_searches: array(BankruptcySearchWithUsers)
    })
);
export type TCaseBankruptcySearchesListFormCodec = typeof CaseBankruptcySearchesListForm;
export type TCaseBankruptcySearchesListForm = TTypeOfCodec<TCaseBankruptcySearchesListFormCodec>;

export const CaseVideoVerificationCallCreateForm: TFormCodec<TCaseIdCodec, TEmptyObjectCodec> = form(
    CaseId,
    EmptyObject,
);
export type TCaseVideoVerificationCallCreateFormCodec = typeof CaseVideoVerificationCallCreateForm;
export type TCaseVideoVerificationCallCreateForm = TTypeOfCodec<TCaseVideoVerificationCallCreateFormCodec>;

// Outcome union narrower than the DB enum: submitting means logged (no `unknown`) and phone has no slot to miss (no `did_not_show`).
export const CasePhoneVerificationCallLogForm: TFormCodec<
    TRequiredCodec<{
        case_id: TUuidCodec,
        user_id: TUuidCodec,
        outcome: TUnionCodec<[
            TLiteralCodec<"pass">,
            TLiteralCodec<"fail">,
            TLiteralCodec<"needs_video_call">,
            TLiteralCodec<"unreachable">,
        ]>,
        client_understood: TUnionCodec<[TNullCodec, TBooleanCodec]>,
        client_not_distracted: TUnionCodec<[TNullCodec, TBooleanCodec]>,
        vulnerability_concerns: TUnionCodec<[
            TLiteralCodec<"unknown">,
            TLiteralCodec<"none">,
            TLiteralCodec<"yes_concerns">,
        ]>,
        vulnerability_concerns_details: TAnyStringCodec,
    }>,
    TEmptyObjectCodec
> = form(
    required({
        case_id: uuid(),
        user_id: uuid(),
        outcome: union([
            literal("pass"),
            literal("fail"),
            literal("needs_video_call"),
            literal("unreachable"),
        ]),
        client_understood: union([nullCodec(), boolean()]),
        client_not_distracted: union([nullCodec(), boolean()]),
        vulnerability_concerns: union([
            literal("unknown"),
            literal("none"),
            literal("yes_concerns"),
        ]),
        vulnerability_concerns_details: string(),
    }),
    EmptyObject,
);
export type TCasePhoneVerificationCallLogFormCodec = typeof CasePhoneVerificationCallLogForm;
export type TCasePhoneVerificationCallLogForm = TTypeOfCodec<TCasePhoneVerificationCallLogFormCodec>;

export const CaseVideoVerificationCallAssociateWithUserForm: TFormCodec<TUserVideoVerificationCall1Codec, TEmptyObjectCodec> = form(
    UserVideoVerificationCall1,
    EmptyObject,
);
export type TCaseVideoVerificationCallAssociateWithUserFormCodec = typeof CaseVideoVerificationCallAssociateWithUserForm;
export type TCaseVideoVerificationCallAssociateWithUserForm = TTypeOfCodec<TCaseVideoVerificationCallAssociateWithUserFormCodec>;

export const CaseVideoVerificationCallUserForm: TFormCodec<TUserVideoVerificationCall2Codec, TIntersectionCodec<[
    TRequiredFlatOverloadedCodec<{
        full_name: TUserNameCodec,
    }>,
    TRequiredFlatOverloadedCodec<{
        has_expired: TUsersVideoVerificationCallHasExpiredCodec,
    }>,
    TRequiredCodec<{
        video_verification_call_id: TUuidCodec,
    }>,
    TRequiredCodec<{
        user_id: TUuidCodec,
    }>,
]>> = form(
    UserVideoVerificationCall2,
    intersection([
        requiredFlatOverloaded({
            full_name: UserName,
        }),
        requiredFlatOverloaded({
            has_expired: UsersVideoVerificationCallHasExpired,
        }),
        required({
            video_verification_call_id: uuid(),
        }),
        required({
            user_id: uuid(),
        }),
    ]),
);
export type TCaseVideoVerificationCallUserFormCodec = typeof CaseVideoVerificationCallUserForm;
export type TCaseVideoVerificationCallUserForm = TTypeOfCodec<TCaseVideoVerificationCallUserFormCodec>;

export const CaseVideoVerificationCallListForm: TFormCodec<
    TVideoVerificationCall1Codec,
    TIntersectionCodec<[
        TVideoVerificationCall2Codec,
        TRequiredCodec<{
            associate_with_user: TCaseVideoVerificationCallAssociateWithUserFormCodec,
            users: TArrayCodec<TCaseVideoVerificationCallUserFormCodec>,
        }>
    ]>
> = form(
    VideoVerificationCall1,
    intersection([
        VideoVerificationCall2,
        required({
            associate_with_user: CaseVideoVerificationCallAssociateWithUserForm,
            users: array(CaseVideoVerificationCallUserForm),
        })
    ]),
);
export type TCaseVideoVerificationCallListFormCodec = typeof CaseVideoVerificationCallListForm;
export type TCaseVideoVerificationCallListForm = TTypeOfCodec<TCaseVideoVerificationCallListFormCodec>;

export const CaseSdltDeclarationForm: TFormCodec<TCaseIdCodec, TEmptyObjectCodec> = form(
    CaseId,
    EmptyObject,
);
export type TCaseSdltDeclarationFormCodec = typeof CaseSdltDeclarationForm;
export type TCaseSdltDeclarationForm = TTypeOfCodec<TCaseSdltDeclarationFormCodec>;

export const CaseSdltForm: TFormCodec<TCasesSdltCodec, TIntersectionCodec<[
    TRequiredFlatOverloadedCodec<{
        status: TCaseSdltAnswersSubmissionStatusCodec,
    }>,
    TRequiredFlatOverloadedCodec<{
        client_names_for_declaration: TUserNamesCodec,
    }>,
    TRequiredFlatOverloadedCodec<{
        submit_declaration: TCaseSdltDeclarationFormCodec,
    }>
]>> = form(
    CasesSdlt,
    intersection([
        requiredFlatOverloaded({
            status: CaseSdltAnswersSubmissionStatus,
        }),
        requiredFlatOverloaded({
            client_names_for_declaration: UserNames,
        }),
        requiredFlatOverloaded({
            submit_declaration: CaseSdltDeclarationForm,
        }),
    ]),
);
export type TCaseSdltFormCodec = typeof CaseSdltForm;
export type TCaseSdltForm = TTypeOfCodec<TCaseSdltFormCodec>;

export const CaseNewRiskAssessmentForm: TFormCodec<TCasesRiskAssessment1Codec, TEmptyObjectCodec> = form(
    CasesRiskAssessment1,
    EmptyObject,
);
export type TCaseNewRiskAssessmentFormCodec = typeof CaseNewRiskAssessmentForm;
export type TCaseNewRiskAssessmentForm = TTypeOfCodec<TCaseNewRiskAssessmentFormCodec>;

export const CaseAuditComments = required({
    audit_comments: longString(),
});
export type TCaseAuditComments = TTypeOfCodec<typeof CaseAuditComments>;
export type TCaseAuditCommentsCodec = typeof CaseAuditComments;

export const CaseAuditChildren = required({
    selected_for_audit: boolean(),
    audit_completed_at: union([nullCodec(), dateTime()]),
    audit_completed_by_user_id: union([nullCodec(), uuid()]),
});
export type TCaseAuditChildren = TTypeOfCodec<typeof CaseAuditChildren>;
export type TCaseAuditChildrenCodec = typeof CaseAuditChildren;

export const SubmitCaseAuditAsCompleteForm: TFormCodec<TCaseIdCodec, TEmptyObjectCodec> = form(
    CaseId,
    EmptyObject
);
export type TSubmitCaseAuditAsCompleteFormCodec = typeof SubmitCaseAuditAsCompleteForm;
export type TSubmitCaseAuditAsCompleteForm = TTypeOfCodec<TSubmitCaseAuditAsCompleteFormCodec>;

export const CaseAuditForm: TFormCodec<
    TIntersectionCodec<[
        TCaseIdCodec,
        TCaseAuditCommentsCodec,
    ]>,
    TIntersectionCodec<[
        TCaseAuditChildrenCodec,
        TRequiredCodec<{
            submit_audit_as_complete: TSubmitCaseAuditAsCompleteFormCodec
        }>
    ]>
> = form(
    intersection([
        CaseId,
        CaseAuditComments,
    ]),
    intersection([
        CaseAuditChildren,
        required({
            submit_audit_as_complete: SubmitCaseAuditAsCompleteForm
        })
    ]),
);
export type TCaseAuditFormCodec = typeof CaseAuditForm;
export type TCaseAuditForm = TTypeOfCodec<typeof CaseAuditForm>;

const CreateQuoteChildrenReferralFees = required({
    purchase: positiveInteger(),
    sale: positiveInteger(),
    transfer: positiveInteger(),
    remortgage: positiveInteger(),
    transfer_and_remortgage: positiveInteger(),
});
export type TCreateQuoteChildrenReferralFees = TTypeOfCodec<typeof CreateQuoteChildrenReferralFees>;

const CreateQuoteChildren = required({
    introducer_name: string(),
    referral_fees: CreateQuoteChildrenReferralFees,
});
export type TCreateQuoteChildrenCodec = typeof CreateQuoteChildren;

export const CreateCaseQuoteUnfilledForm: TFormCodec<TCaseCreateQuoteUnfilledCodec, TCreateQuoteChildrenCodec> = form(
    CaseCreateQuoteUnfilled,
    CreateQuoteChildren,
);
export type TCreateCaseQuoteUnfilledFormCodec = typeof CreateCaseQuoteUnfilledForm;
export type TCreateCaseQuoteUnfilledForm = TTypeOfCodec<TCreateCaseQuoteUnfilledFormCodec>;

export const CreateCaseQuoteFilledForm: TFormCodec<TCaseCreateQuoteFilledCodec, TCreateQuoteChildrenCodec> = form(
    CaseCreateQuoteFilled,
    CreateQuoteChildren,
);
export type TCreateCaseQuoteFilledFormCodec = typeof CreateCaseQuoteFilledForm;
export type TCreateCaseQuoteFilledForm = TTypeOfCodec<TCreateCaseQuoteFilledFormCodec>;

export const CaseQuoteSentToClientForm: TFormCodec<TCaseQuoteSentToClientCodec, TEmptyObjectCodec> = form(
    CaseQuoteSentToClient,
    EmptyObject,
);
export type TCaseQuoteSentToClientFormCodec = typeof CaseQuoteSentToClientForm;
export type TCaseQuoteSentToClientForm = TTypeOfCodec<TCaseQuoteSentToClientFormCodec>;

export const CaseQuoteAddAdditionalItemForm: TFormCodec<TCaseQuoteAddAdditionalItemCodec, TEmptyObjectCodec> = form(
    CaseQuoteAddAdditionalItem,
    EmptyObject,
);
export type TCaseQuoteAddAdditionalItemFormCodec = typeof CaseQuoteAddAdditionalItemForm;
export type TCaseQuoteAddAdditionalItemForm = TTypeOfCodec<TCaseQuoteAddAdditionalItemFormCodec>;

export const CaseQuoteRemoveAdditionalItemForm: TFormCodec<TCaseQuoteRemoveAdditionalItemCodec, TEmptyObjectCodec> = form(
    CaseQuoteRemoveAdditionalItem,
    EmptyObject,
);
export type TCaseQuoteRemoveAdditionalItemFormCodec = typeof CaseQuoteRemoveAdditionalItemForm;
export type TCaseQuoteRemoveAdditionalItemForm = TTypeOfCodec<TCaseQuoteRemoveAdditionalItemFormCodec>;

const CaseFormChildren: TIntersectionCodec<[
    TRequiredCodec<{
        members: TArrayCodec<TCaseMemberFormCodec>,
        bank_accounts: TArrayCodec<TCaseBankAccountFormCodec>,
        // The case's introducer offered as a payout recipient — present only when the
        // case has an introducer AND that introducer has bank details on file. Read-only;
        // feeds the staff "Send proceeds to" dropdown option + the return-to-introducer note.
        bank_account_introducer_option: TUnionCodec<[TBankAccountIntroducerCodec, TNullCodec]>,
        sign_off_as_ready_for_exchange: TCaseSignOffAsReadyForExchangeFormCodec,
        instruct: TCaseInstructFormCodec,
        related_transaction: TRequiredCodec<{
            create_link: TCaseRelatedTransactionCreateFormCodec,
            related_data: TCaseRelatedTransactionDataFormCodec,
        }>,
        case_users_bankruptcy_searches: TRequiredCodec<{
            list: TCaseBankruptcySearchesListFormCodec,
            case_users_requiring_bankruptcy_searches: TArrayCodec<TUserBankruptcySearchCodec>
        }>,
        video_verification_call: TRequiredCodec<{
            create: TCaseVideoVerificationCallCreateFormCodec,
            list: TArrayCodec<TCaseVideoVerificationCallListFormCodec>,
            users_that_can_be_booked_for_video_call: TArrayCodec<TUserBookableForVideoCallCodec>,
        }>,
        sdlt: TCaseSdltFormCodec,
        risk_assessments: TRequiredCodec<{
            new_form: TCaseNewRiskAssessmentFormCodec,
            assessments: TArrayCodec<TCasesRiskAssessment2Codec>,
            new_assessment_required: TBooleanCodec,
        }>,
        audit: TCaseAuditFormCodec,
        introducers: TArrayCodec<TIntroducer1Codec>,
        dual_rep: TRequiredCodec<{
            create_link: TCaseDualRepCreateFormCodec,
            linked_case_form: TUnionCodec<[
                TCaseDualRepLinkedFormCodec,
                TNullCodec,
            ]>,
        }>,
        ready_for_exchange_checklist: TReadyForExchangeChecklistCodec,
        quotes: TArrayCodec<TCaseQuoteCodec>,
        create_quote_form: TCreateCaseQuoteUnfilledFormCodec,
        quote_sent_to_client_form: TCaseQuoteSentToClientFormCodec,
        quote_additional_item_options: TArrayCodec<TQuoteAdditionalItemOptionCodec>,
        quote_add_additional_item_form: TCaseQuoteAddAdditionalItemFormCodec,
        quote_remove_additional_item_form: TCaseQuoteRemoveAdditionalItemFormCodec,
        epc: TRequiredCodec<{
            data: TUnionCodec<[TCasesEpc1Codec, TNullCodec]>,
            search: TCaseEpcSearchFormCodec,
            attach: TCaseEpcAttachFormCodec,
            rerun: TCaseEpcRerunLookupFormCodec,
            mark_not_required: TCaseEpcMarkNotRequiredFormCodec,
            set_sorted: TCaseEpcSetSortedFormCodec,
            property_postcode: TAnyStringCodec,
            has_received_epc_document: TBooleanCodec,
        }>,
    }>,
    TCaseRootReadOnlyPropsCodec,
    TCaseEnquiryTimestampsCodec,
]> = intersection([
    required({
        members: array(CaseMemberForm),
        bank_accounts: array(CaseBankAccountForm),
        bank_account_introducer_option: union([BankAccountIntroducer, nullCodec()]),
        sign_off_as_ready_for_exchange: CaseSignOffAsReadyForExchangeForm,
        instruct: CaseInstructForm,
        related_transaction: required({
            create_link: CaseRelatedTransactionCreateForm,
            related_data: CaseRelatedTransactionDataForm,
        }),
        case_users_bankruptcy_searches: required({
            list: CaseBankruptcySearchesListForm,
            case_users_requiring_bankruptcy_searches: array(UserBankruptcySearch)
        }),
        video_verification_call: required({
            create: CaseVideoVerificationCallCreateForm,
            list: array(CaseVideoVerificationCallListForm),
            users_that_can_be_booked_for_video_call: array(UserBookableForVideoCall),
        }),
        sdlt: CaseSdltForm,
        risk_assessments: required({
            new_form: CaseNewRiskAssessmentForm,
            assessments: array(CasesRiskAssessment2),
            new_assessment_required: boolean(),
        }),
        audit: CaseAuditForm,
        introducers: array(Introducer1),
        dual_rep: required({
            create_link: CaseDualRepCreateForm,
            linked_case_form: union([CaseDualRepLinkedForm, nullCodec()]),
        }),
        ready_for_exchange_checklist: ReadyForExchangeChecklist,
        quotes: array(CaseQuote),
        create_quote_form: CreateCaseQuoteUnfilledForm,
        quote_sent_to_client_form: CaseQuoteSentToClientForm,
        quote_additional_item_options: array(QuoteAdditionalItemOption),
        quote_add_additional_item_form: CaseQuoteAddAdditionalItemForm,
        quote_remove_additional_item_form: CaseQuoteRemoveAdditionalItemForm,
        epc: required({
            data: union([CasesEpc1, nullCodec()]),
            search: CaseEpcSearchForm,
            attach: CaseEpcAttachForm,
            rerun: CaseEpcRerunLookupForm,
            mark_not_required: CaseEpcMarkNotRequiredForm,
            set_sorted: CaseEpcSetSortedForm,
            property_postcode: string(),
            has_received_epc_document: boolean(),
        }),
    }),
    CaseRootReadOnlyProps,
    CaseEnquiryTimestamps,
]);
type TCaseFormChildrenCodec = typeof CaseFormChildren;

export const CaseForm: TFormCodec<TCaseEditableFullCodec, TCaseFormChildrenCodec> = form(
    CaseEditableFull,
    CaseFormChildren,
);
export type TCaseFormCodec = typeof CaseForm;
export type TCaseForm = TTypeOfCodec<TCaseFormCodec>;
